import { useState, useRef, useCallback, useEffect } from 'react'
import { computeWaveformFromBlob } from '../../audioWaveform'

export type RecordingState = 'idle' | 'recording' | 'stopped'

const MIME_TYPE_PREFERENCE = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
  'audio/webm;codecs=opus',
  'audio/webm',
]

interface UseAudioRecorderReturn {
  recordingState: RecordingState
  recordingTime: number
  audioBlob: Blob | null
  audioUrl: string | null
  // Normalized 0..1 amplitude samples computed from the recorded blob.
  // Populated asynchronously after stopRecording / sendRecording so the
  // preview waveform reflects the actual audio.
  waveformSamples: number[] | null
  startRecording: () => Promise<void>
  stopRecording: () => void
  sendRecording: (onComplete: (blob: Blob, duration: number) => void) => void
  cancelRecording: () => void
  error: string | null
}

export const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle')
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [waveformSamples, setWaveformSamples] = useState<number[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const onStopCallbackRef = useRef<((blob: Blob, duration: number) => void) | null>(null)
  const recordingTimeRef = useRef<number>(0)
  const recordingMimeTypeRef = useRef<string>('')
  const startRequestIdRef = useRef<number>(0)
  // Set when stop() is being called as part of a cancel, so the queued
  // onstop handler knows to discard the chunks instead of producing a blob.
  const cancelledRef = useRef<boolean>(false)
  // Tags each waveform decode so a late-arriving result from a previous
  // recording can't overwrite the current one (cancel + record again).
  const waveformRequestIdRef = useRef<number>(0)

  useEffect(() => {
    recordingTimeRef.current = recordingTime
  }, [recordingTime])

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioUrl])

  useEffect(() => {
    if (recordingState !== 'recording') {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1)
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [recordingState])

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === 'undefined') {
      setError('Audio recording is not supported in this browser')
      setRecordingState('idle')
      return
    }

    try {
      setError(null)
      // Bump the request id so any prior in-flight startRecording (e.g.
      // a Strict Mode re-mount) knows its result is stale and must be discarded.
      const requestId = ++startRequestIdRef.current
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (requestId !== startRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      streamRef.current = stream

      const selectedMimeType = MIME_TYPE_PREFERENCE.find((type) => MediaRecorder.isTypeSupported(type)) || ''
      recordingMimeTypeRef.current = selectedMimeType

      const mediaRecorder = new MediaRecorder(
        stream,
        selectedMimeType ? { mimeType: selectedMimeType } : undefined,
      )

      if (!recordingMimeTypeRef.current) {
        recordingMimeTypeRef.current = mediaRecorder.mimeType
      }

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []
      cancelledRef.current = false

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        if (cancelledRef.current) {
          cancelledRef.current = false
          chunksRef.current = []
          return
        }
        const mimeType = recordingMimeTypeRef.current || mediaRecorderRef.current?.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: mimeType })
        const url = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(url)
        setRecordingState('stopped')

        const waveformRequestId = ++waveformRequestIdRef.current
        void computeWaveformFromBlob(blob).then((samples) => {
          if (waveformRequestId !== waveformRequestIdRef.current) return
          if (samples && samples.length > 0) setWaveformSamples(samples)
        })

        if (onStopCallbackRef.current) {
          onStopCallbackRef.current(blob, recordingTimeRef.current)
          onStopCallbackRef.current = null
        }
      }

      mediaRecorder.start()
      setRecordingTime(0)
      setAudioBlob(null)
      setAudioUrl(null)
      setWaveformSamples(null)
      // Bump the waveform id so any late decode from a previous recording
      // is treated as stale and dropped.
      waveformRequestIdRef.current++
      setRecordingState('recording')
    } catch (err) {
      console.error('Error starting recording:', err)
      setError(err instanceof Error ? err.message : 'Failed to start recording')
      setRecordingState('idle')
    }
  }, [])

  // End the recording without sending — transitions to 'stopped' so the
  // user can preview / send / discard. Mirrors mobile's pauseRecording in
  // useChatAudioController, which finalises the recording and enters
  // preview mode (no resume — QA on PR 1359 required the same on web).
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    // Drop the mic synchronously so the tab "in use" indicator clears
    // without waiting for onstop.
    releaseStream()
    // Flip the UI to preview synchronously; onstop will populate the blob
    // a microtask later (and reassert this same state, which is fine).
    setRecordingState('stopped')
  }, [releaseStream])

  // Send the recording. If the blob is already available (preview mode),
  // fires onComplete right away. Otherwise stops the recorder and lets the
  // onstop handler deliver the blob to onComplete.
  const sendRecording = useCallback(
    (onComplete: (blob: Blob, duration: number) => void) => {
      if (audioBlob) {
        onComplete(audioBlob, recordingTimeRef.current)
        return
      }
      onStopCallbackRef.current = onComplete
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      releaseStream()
      setRecordingState('stopped')
    },
    [audioBlob, releaseStream],
  )

  const cancelRecording = useCallback(() => {
    // Unconditional: state may already be 'inactive' if a prior stop()/
    // sendRecording() ran in the same tick. The queued onstop is still
    // pending in that case, so marking cancelled and nulling the callback
    // must happen regardless of the live state to suppress the queued task.
    cancelledRef.current = true
    onStopCallbackRef.current = null
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    chunksRef.current = []
    setAudioBlob(null)
    setAudioUrl(null)
    setWaveformSamples(null)
    waveformRequestIdRef.current++
    releaseStream()
    setRecordingTime(0)
    setRecordingState('idle')
    setError(null)
  }, [releaseStream])

  return {
    recordingState,
    recordingTime,
    audioBlob,
    audioUrl,
    waveformSamples,
    startRecording,
    stopRecording,
    sendRecording,
    cancelRecording,
    error,
  }
}
